import {EventDispatcher} from './EventDispatcher.js';
import {
  CHANGE
} from './constants.js';
import {areJSONEqual} from './utils.js';

/**
 * WatchableObject is a standard key/value datastore that dispatches a CHANGE event when any of its
 * property values change. Usage:
 *
 * let o = new WatchableObject();
 * // Register for property-value changes
 * o.on(CHANGE, (e) => {
 *   console.log(`Property ${e.detail.prop} changed from ${e.detail.oldValue} to ${e.detail.value}`);
 * }
 * // Assign x, which triggers the invocation of the preceding CHANGE event listener
 * o.x = 10;
 *
 * To assign a value to a property on a WatchableObject WITHOUT triggering a CHANGE event, use
 * the WatchableObject's quietSet() method, as in:
 * o.quietSet('secret', 'some private data');
 *
 * WatchableObject is useful as a base class for models in the model/view/controller pattern.
 *
 * Design note:
 * WatchableObject was selected over WatchableMap for the following reasons:
 *   -MegaScreen does not need Map's ordered iteration
 *   -MegaScreen does not need to use objects as keys (which would require Map)
 *   -objects can be converted to JSON natively, Maps cannot
 *   -object property assignment/retrieval is more readable than Map set()/get():
 *     o.points = 230        // Object
 *     m.set('points', 230)  // Map
 *     o.points += 10        // Object
 *     m.set('points', m.get('points') + 10)  // Map
 */
export class WatchableObject extends EventDispatcher {
  constructor () {
    super();

    /**
     * **** Important note on the return value of the WatchableObject constructor ****
     *
     * In JavaScript's class system (and underlying prototype-based system), a class's
     * constructor function can legally return an instance of a different class. In some languages,
     * returning a different type of object from a constructor is forbidden, and even in JavaScript,
     * some curators of the language consider this behavior inappropriate, and a possible candidate
     * for future deprecation or removal. However, Brendan Eich, the creator of JavaScript, supports
     * the concept of returning an arbitrary value from JavaScript constructor functions, so the
     * functionality is unlikely to be removed. In the case of WatchableObject, JavaScript's flexibility
     * on constructor-return values provides the only means of wrapping a new WatchableObject instance
     * in a Proxy such that all property assignments can be intercepted for the purpose of dispatching
     * CHANGE events.
     *
     * If the ability to return a Proxy object from the WatchableObject constructor were ever
     * removed from JavaScript, the code in this constructor would require a minor rewrite to move
     * property assignment to a single proxied property, such as, say, .data. For example, the
     * WatchableObject constructor could define the proxied .data property as follows:
     *
     *   this.data = new Proxy(this, proxyHandler);
     *
     * Then, all WatchableObject assignments throughout MegaScreen would have to modified to use
     * the .data property instead of the WatchableObject instance, itself, as in:
     *
     *   let o = new WatchableObject();
     *   o.on(CHANGE, (e) => {
     *     console.log(`Caught CHANGE on o.data. Changed Property: ${e.detail.prop}. New Value: ${e.detail.value}`);
     *   });
     *   // ASSIGN ON .data, not o
     *   o.data.x = 10;
     *
     * A change in JavaScript's behavior is, however, not expected, so the preceding rewrite
     * of WatchableObject will likely never become necessary. As another alternative implementation
     * for a watchable datastore, a "WatchableMap" class could be implemented based on the following
     * rough sketch:
     *
     *   import {EventDispatcher} from '@megaphonenpm/mputils/src/EventDispatcher.js';
     *   import {CHANGE} from "./constants.js";
     *
     *   export class WatchableMap extends Map {
     *     constructor () {
     *       super();
     *       this.eventDispatcher = new EventDispatcher();
     *     }
     *
     *     set (key, value) {
     *       super.set(key, value);
     *       this.dispatchChange(key, value);
     *     }
     *
     *     quietSet (key, value) {
     *       super.set(key, value);
     *     }
     *
     *     addChangeListener (listener) {
     *       this.eventDispatcher.on(CHANGE, listener);
     *     }
     *
     *     removeChangeListener (listener) {
     *       this.eventDispatcher.off(CHANGE, listener);
     *     }
     *
     *     dispatchChange (key, value) {
     *       this.eventDispatcher.dispatchEvent(CHANGE, {prop: key, value: value});
     *     }
     *   }
     *
     * **** Further Reading ****
     * Brendan Eich and TC39 discuss constructor return values:
     * https://esdiscuss.org/topic/should-the-default-constructor-return-the-return-value-of-super
     *
     * Extending the Proxy class:
     * https://stackoverflow.com/questions/37714787/can-i-extend-proxy-with-an-es2015-class
     */
    return new Proxy(this, proxyHandler);
  }

  /**
   * Assigns a property value without triggering a CHANGE event.
   */
  quietSet (prop, value) {
    let set = proxyHandler.set;
    delete proxyHandler.set;
    this[prop] = value;
    proxyHandler.set = set;
  }

  /**
   * The setPrimitiveProperty() method is an alternative to direct-property assignment for primitive
   * values, with the only difference being that if the existing property value is the same as the
   * supplied newValue, then assignment is aborted and no CHANGE event is dispatched.
   *
   * If the supplied primitive newValue is different from the value of the property specified by
   * name, setPrimitiveProperty() assigns the newValue to that property. If quiet is true when
   * the value is assigned, a CHANGE event is dispatched. If quiet is false when the value is
   * assigned, or if the old and new values are the same, setPrimitiveProperty() does nothing.
   *
   * @param name
   * @param newValue
   * @param quiet
   */
  setPrimitiveProperty (name, newValue, quiet = false) {
    if (this[name] !== newValue) {
      if (quiet) {
        this.quietSet(name, newValue);
      } else {
        this[name] = newValue;
      }
    }
  }

  /**
   * The setCompositeProperty() method is an alternative to direct-property assignment for
   * non-primitive values, with the only difference being that if the existing property value is the
   * same as the supplied newValue, then assignment is aborted and no CHANGE event is dispatched.
   *
   * If the supplied newValue is different from the value of the property specified by name,
   * setCompositeProperty() assigns the newValue to that property. If quiet is true when
   * the value is assigned, a CHANGE event is dispatched. If quiet is false when the value is
   * assigned, or if the old and new values are the same, setCompositeProperty() does nothing.
   *
   * @param name
   * @param newValue
   * @param quiet
   */
  setCompositeProperty (name, newValue, quiet = false) {
    if (!areJSONEqual(this[name], newValue)) {
      if (quiet) {
        this.quietSet(name, newValue);
      } else {
        this[name] = newValue;
      }
    }
  }
}

/**
 * Helper Proxy that intercepts property assignments and dispatches the CHANGE event. This
 * Proxy is used by ALL WatchableObject instances.
 */
let proxyHandler = {
  set: (obj, prop, value) => {
    let oldValue = obj[prop];
    obj[prop] = value;
    obj.dispatchEvent(CHANGE, {prop: prop, value: value, oldValue: oldValue});
    return true;
  }
};
